// The bell (30-day list row 19): the person's alerts, live, shared by the shell's badge and the
// Alerts screen through one listener; grouped by project with the unread first.
import { useEffect, useState } from 'react';
import { collection, doc, limit, onSnapshot, orderBy, query, writeBatch } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth, type Session } from '../auth';

export interface Alert { id: string; kind: string; pid: string | null; page: string | null; link: string | null; itemLabel: string; text: string; read: boolean; at: number | null }

const alertsPath = (s: Session) => `${s.paths.users}/${s.user.uid}/alerts`;
// One listener per signed-in person, refcounted across the components that read it.
const store: { uid: string | null; list: Alert[]; subs: Set<(l: Alert[]) => void>; stop: null | (() => void) } = { uid: null, list: [], subs: new Set(), stop: null };
function attach(s: Session) {
  if (store.uid === s.user.uid && store.stop) return;
  store.stop?.(); store.uid = s.user.uid; store.list = [];
  const q = query(collection(db, alertsPath(s)), orderBy('at', 'desc'), limit(200));
  store.stop = onSnapshot(q, (snap) => {
    store.list = snap.docs.map(d => { const x = d.data(); const at = x.at && typeof x.at.toMillis === 'function' ? x.at.toMillis() : null; return { id: d.id, kind: String(x.kind || 'system'), pid: (x.pid as string) || null, page: (x.page as string) || null, link: (x.link as string) || (x.href as string) || null, itemLabel: String(x.itemLabel || x.title || ''), text: String(x.text || x.body || ''), read: !!x.read, at }; });
    store.subs.forEach(f => f(store.list));
  }, (e) => { console.warn('alerts', e); });
}
function detachIfIdle() { if (store.subs.size === 0) { store.stop?.(); store.stop = null; store.uid = null; } }

export function useAlerts(): Alert[] {
  const { session } = useAuth();
  const [list, setList] = useState<Alert[]>(() => store.list);
  useEffect(() => {
    if (!session) return;
    attach(session); store.subs.add(setList); setList(store.list);
    return () => { store.subs.delete(setList); setTimeout(detachIfIdle, 2000); };
  }, [session]);
  return list;
}
export const unreadCount = (list: Alert[]) => list.filter(a => !a.read).length;

export async function markRead(s: Session, ids: string[]) {
  if (!ids.length) return;
  const b = writeBatch(db);
  for (const id of ids) b.update(doc(db, `${alertsPath(s)}/${id}`), { read: true });
  await b.commit();
}

export interface AlertGroup { pid: string | null; project: string; unread: number; latest: number; items: Alert[] }
/** Groups by project, unread groups first, then by the newest alert; a group's items newest first. */
export function groupAlerts(list: Alert[], nameOf: (pid: string | null) => string): AlertGroup[] {
  const by = new Map<string, AlertGroup>();
  for (const a of list) {
    const k = a.pid || '';
    let g = by.get(k); if (!g) { g = { pid: a.pid, project: nameOf(a.pid), unread: 0, latest: 0, items: [] }; by.set(k, g); }
    g.items.push(a); if (!a.read) g.unread++; g.latest = Math.max(g.latest, a.at ?? 0);
  }
  return [...by.values()].sort((x, y) => Number(y.unread > 0) - Number(x.unread > 0) || y.latest - x.latest);
}

/** A plain word for each kind the server writes; unknown kinds read as their own name. */
const KIND_WORDS: Record<string, string> = {
  'punch-assigned': 'Punch item for you', 'task-behind': 'Plan row running late', 'milestone-reached': 'Milestone reached', 'requisition': 'Material request', 'requisition-waiting': 'Request waiting', 'requisition-ordered': 'Request ordered', 'request-staged': 'Request staged', 'request-run': 'On the run', 'run-driver': 'You are driving', 'run-closed': 'Run closed',
  'tool-request': 'Tool request', 'tool-request-done': 'Tools ready', 'tool-out': 'Tool checked out', 'tool-problem': 'Tool problem', 'tool-sweep': 'Tools overdue', 'controls-sent': 'Sent to the GC', 'co-approved': 'Change order decided', 'tm-log-filed': 'T&M day filed', 'response': 'GC responded', 'mail-sent': 'Mail sent', 'inbound': 'Mail came in', 'thread-reply': 'Reply on the channel',
  'timeclock': 'Time clock', 'time-fix': 'Punch fix', 'timefix': 'Punch fix', 'stale-clockin': 'Still clocked in', 'week-approved': 'Week approved', 'schedule': 'Schedule', 'labor': 'Labor', 'drift': 'Drifting off plan', 'brief': 'Morning brief', 'bid-due': 'Bid due', 'bid-won': 'Bid won', 'signature': 'Signed', 'sheet-added': 'Sheet added',
  'service': 'Service', 'service-stop': 'Service stop', 'service-paid': 'Service paid', 'service-done': 'Service done', 'service-morning': 'Service today', 'team-joined': 'Joined the team', 'team-nopush': 'No pushes on a phone', 'system': 'TradeView',
};
export const kindWord = (k: string) => KIND_WORDS[k] || k.replace(/[-_]/g, ' ').replace(/^\w/, c => c.toUpperCase());
